import React, { Component } from 'react';
import { withStyles, WithStyles } from '@mui/styles';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, Outlet, NavigateFunction } from 'react-router';
import * as stApi from '../api/station-tools-api';
import * as eventsActions from '../store/events.actions';
import * as selectors from '../store/selectors';
import * as airportsActions from '../store/airports.actions';
import * as authActions from '../store/auth.actions';
import { RootState } from '../store/reducers';

import { Fab, Tooltip, Theme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Page from '../components/Page';
import Calendar from '../components/Calendar/Calendar';
import AddEventDialog from '../components/AddEventDialog/AddEventDialog';

// FIXME: Convert to functional component
class InternalMainPage extends Component<
  WithStyles<typeof styles> &
    ConnectedProps<typeof connector> & {
      navigate: NavigateFunction;
    },
  {
    isSlotDialogOpen: boolean;
  }
> {
  state = {
    isSlotDialogOpen: false,
  };

  toggleSlotDialog = (): void => {
    this.setState({ isSlotDialogOpen: !this.state.isSlotDialogOpen });
  };

  handleAddEventClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    this.toggleSlotDialog();
  };

  handleClose = (): void => {
    this.toggleSlotDialog();
  };

  handleRangeChange = async (
    range: Date[] | { start: Date; end: Date }
  ): Promise<void> => {
    const { airports, getAirports, getEvents } = this.props;
    if (!airports.length) await getAirports();
    let eventsRange: { start: Date; end: Date };

    if (range instanceof Array) {
      const end = new Date(range[range.length - 1]);
      end.setHours(23, 59, 59, 999);
      eventsRange = {
        start: range[0],
        end: end,
      };
    } else {
      eventsRange = {
        start: range.start,
        end: range.end,
      };
    }

    await getEvents(eventsRange);
  };

  handleEventSelect = (eventId: string): void => {
    // FIXME try to use relative path
    this.props.navigate(`events/${eventId}`);
  };

  handleAirportFilterChange = (name: string, selected: boolean): void => {
    const { selectAirport } = this.props;
    selectAirport(name, selected);
  };

  handleAirportFilterClear = (): void => {
    const { clearAirportsFilter } = this.props;
    clearAirportsFilter();
  };

  handleSubmit = async (slot: unknown): Promise<void> => {
    const { getEvents } = this.props;
    this.toggleSlotDialog();
    await stApi.addSlot(slot);
    await getEvents();
  };

  render() {
    const { classes, events, airports, logout } = this.props;
    const { isSlotDialogOpen } = this.state;
    return (
      <Page>
        <Calendar
          events={events}
          airports={airports}
          onRangeChange={this.handleRangeChange}
          onEventSelect={this.handleEventSelect}
          onAirportFilterChange={this.handleAirportFilterChange}
          onAirportFilterClear={this.handleAirportFilterClear}
          onLogout={logout}
        />
        <Tooltip title="Add Event" disableFocusListener>
          <Fab
            color="primary"
            aria-label="Add"
            className={classes.cornerButton}
            onClick={this.handleAddEventClick}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
        <AddEventDialog
          isOpen={isSlotDialogOpen}
          airports={airports}
          onClose={this.handleClose}
          onSubmit={this.handleSubmit}
          onGenerateSlots={stApi.generateSlots}
        />
        <Outlet />
      </Page>
    );
  }
}

const styles = (theme: Theme) => ({
  cornerButton: {
    position: 'absolute' as const,
    bottom: theme.spacing(2),
    right: theme.spacing(3),
    zIndex: 1000,
    opacity: 0.9,
  },
});

const connector = connect(
  (state: RootState) => ({
    events: selectors.getFilteredEvents(state),
    airports: selectors.getAirports(state),
  }),
  {
    getEvents: eventsActions.getEvents,
    getAirports: airportsActions.getAirports,
    selectAirport: airportsActions.selectAirport,
    clearAirportsFilter: airportsActions.clearAirportsFilter,
    logout: authActions.logout,
  }
);

const StyledMainPage = withStyles(styles)(InternalMainPage);

const UnconnectedMainPage = (props: ConnectedProps<typeof connector>) => {
  const navigate = useNavigate();
  return <StyledMainPage {...props} navigate={navigate} />;
};

export const MainPage = connector(UnconnectedMainPage);
